import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	FEATURE_CONFIG,
	enhanceImageViaBackend,
	generateImagesViaBackend,
	MAX_FILE_SIZE,
	DEFAULT_FEATURE_OPTIONS,
	sortSelectedFeatures, 
	getErrorMessage, 
	getPromptsForProductCategory
} from "../../../utils/imageEditor";

const buildCatalogPromptFromProduct = (title, description) => {
	const t = (title || "").trim();
	const d = (description || "").trim();
	if (!t && !d) return "";
	const productPart = [t, d].filter(Boolean).join(". ");
	return `Professional catalog or menu photo: ${productPart}. Soft natural lighting, clean neutral background, appetizing food photography style, high resolution, centered composition, suitable for restaurant or kitchen menu listing.`;
};

export function useAiImageGenerator(productCategory, title = "", description = "") {
	const [mode, setMode] = useState("enhance");
	const [imageFile, setImageFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [selectedFeatures, setSelectedFeatures] = useState([]);
	const [featureOptions, setFeatureOptions] = useState(DEFAULT_FEATURE_OPTIONS);
	const catalogPromptFromProduct = useMemo(
		() => buildCatalogPromptFromProduct(title, description),
		[title, description]
	);
	const [genPrompt, setGenPrompt] = useState(catalogPromptFromProduct || "");
	const [genSeed, setGenSeed] = useState("");
	const [generatedUrls, setGeneratedUrls] = useState([]);
	const [useExportAndResolution, setUseExportAndResolution] = useState(false);
	const [exportFormat, setExportFormat] = useState("jpeg");
	const [exportQuality, setExportQuality] = useState(90);
	const [globalOutputSize, setGlobalOutputSize] = useState("1000x1000");
	const [maxWidth, setMaxWidth] = useState("500");
	const [maxHeight, setMaxHeight] = useState("500");
	const [referenceBox, setReferenceBox] = useState("originalImage");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [processedUrls, setProcessedUrls] = useState([]);
	const [isDragOver, setIsDragOver] = useState(false);
	const [expandAiBackgroundConflictNote, setExpandAiBackgroundConflictNote] = useState(false);

	const abortRef = useRef(null);
	const fileInputRef = useRef(null);

	const handleFile = useCallback((file) => {
		if (!file.type.startsWith("image/")) {
			setError("Please upload a valid image file.");
			return;
		}
		if (file.size > MAX_FILE_SIZE) {
			setError("File size exceeds 10 MB limit.");
			return;
		}
		setError(null);
		setProcessedUrls([]);
		setImageFile(file);
		setPreviewUrl(URL.createObjectURL(file));
	}, []);

	const onFileChange = useCallback(
		(e) => {
			const file = e.target.files?.[0];
			if (file) handleFile(file);
			e.target.value = "";
		},
		[handleFile]
	);

	const onDrop = useCallback(
		(e) => {
			e.preventDefault();
			setIsDragOver(false);
			const file = e.dataTransfer.files[0];
			if (file) handleFile(file);
		},
		[handleFile]
	);

	const clearImage = useCallback(() => {
		setImageFile(null);
		setPreviewUrl(null);
		setProcessedUrls([]);
		setGeneratedUrls([]);
		setError(null);
	}, []);

	const getOptionsForFeature = useCallback(
		(featureId) => {
			switch (featureId) {
				case "shadow":
					return {
						backgroundColor: featureOptions.backgroundColor ?? "#ffffff",
						padding: featureOptions.padding ?? "0.1",
					};
				case "aiBackground":
					return { prompt: featureOptions.prompt ?? "" };
				case "expand": {
					const w = featureOptions.expandWidth?.trim() || "";
					const h = featureOptions.expandHeight?.trim() || "";
					const outputSize =
						w && h
							? `${w}x${h}`
							: useExportAndResolution
							? globalOutputSize
							: "1000x1000";
					return { outputSize };
				}
				default:
					return {};
			}
		},
		[
			featureOptions.backgroundColor,
			featureOptions.expandHeight,
			featureOptions.expandWidth,
			featureOptions.padding,
			featureOptions.prompt,
			globalOutputSize,
			useExportAndResolution,
		]
	);

	const exportOptions = useMemo(
		() =>
			useExportAndResolution
				? {
						format: exportFormat,
						...(exportFormat === "jpeg" && { quality: exportQuality }),
				  }
				: undefined,
		[exportFormat, exportQuality, useExportAndResolution]
	);

	const toggleFeature = useCallback((featureId) => {
		const isDeselecting = selectedFeatures.includes(featureId);
		if (!isDeselecting) {
			// Expand cannot be used with AI Background or AI Shadow
			if (featureId === "expand" && (selectedFeatures.includes("aiBackground") || selectedFeatures.includes("shadow"))) {
				setExpandAiBackgroundConflictNote(true);
				return;
			}
			if ((featureId === "aiBackground" || featureId === "shadow") && selectedFeatures.includes("expand")) {
				setExpandAiBackgroundConflictNote(true);
				return;
			}
		}
		setSelectedFeatures((prev) =>
			prev.includes(featureId)
				? prev.filter((id) => id !== featureId)
				: sortSelectedFeatures([...prev, featureId])
		);
		setProcessedUrls([]);
		setError(null);
	}, [selectedFeatures]);

	const commonOptions = useMemo(
		() => ({
			maxWidth: maxWidth ? Number(maxWidth) || undefined : undefined,
			maxHeight: maxHeight ? Number(maxHeight) || undefined : undefined,
			referenceBox: referenceBox || undefined,
		}),
		[maxHeight, maxWidth, referenceBox]
	);

	const handleApply = useCallback(async () => {
		if (!imageFile || selectedFeatures.length === 0) return;
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		setProcessedUrls(Array(3).fill(null));
		const steps = selectedFeatures.map((featureId) => ({
			featureId,
			options: getOptionsForFeature(featureId),
		}));
		try {
			const urls = await enhanceImageViaBackend(
				imageFile,
				steps,
				exportOptions,
				commonOptions,
				controller.signal
			);
			setProcessedUrls(urls);
		} catch (err) {
			const msg = getErrorMessage(err);
			if (msg) setError(msg);
		} finally {
			setLoading(false);
		}
	}, [commonOptions, exportOptions, getOptionsForFeature, imageFile, selectedFeatures]);

	const handleApplyGenerate = useCallback(async () => {
		if (!genPrompt.trim()) return;
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		setGeneratedUrls(Array(3).fill(null));
		try {
			const urls = await generateImagesViaBackend(
				{
					prompt: genPrompt.trim(),
					seed: genSeed ? Number(genSeed) || undefined : undefined,
					exportOptions,
				},
				controller.signal
			);
			setGeneratedUrls(urls);
		} catch (err) {
			const msg = getErrorMessage(err);
			if (msg) setError(msg);
		} finally {
			setLoading(false);
		}
	}, [exportOptions, genPrompt, genSeed]);

	const handleDownload = useCallback(
		(url) => {
			const ext = useExportAndResolution
				? exportFormat === "jpeg"
					? "jpg"
					: exportFormat
				: "png";
			const name = Math.random().toString(36).slice(2, 10);
			const a = document.createElement("a");
			a.href = url;
			a.download = `ai-image-${name}.${ext}`;
			a.click();
		},
		[exportFormat, useExportAndResolution]
	);

	const setOption = useCallback(
		(key, value) => setFeatureOptions((prev) => ({ ...prev, [key]: value })),
		[]
	);

	const switchToEnhance = useCallback(() => {
		setMode("enhance");
		setError(null);
		setGeneratedUrls([]);
	}, []);

	const switchToGenerate = useCallback(() => {
		setMode("generate");
		setError(null);
		setProcessedUrls([]);
	}, []);

	const resetGenerated = useCallback(() => {
		setGeneratedUrls([]);
		setError(null);
	}, []);

	const resetProcessed = useCallback(() => {
		setProcessedUrls([]);
		setError(null);
	}, []);

	useEffect(() => () => abortRef.current?.abort(), []);

	useEffect(() => {
		const next = buildCatalogPromptFromProduct(title, description);
		if (next) setGenPrompt(next);
	}, [title, description]);

	useEffect(() => {
		if (!expandAiBackgroundConflictNote) return;
		const t = setTimeout(() => setExpandAiBackgroundConflictNote(false), 10000);
		return () => clearTimeout(t);
	}, [expandAiBackgroundConflictNote]);

	const features = useMemo(() => Object.values(FEATURE_CONFIG), []);
	const { category: matchedCategory, prompts: categoryPrompts } = useMemo(
		() => getPromptsForProductCategory(productCategory),
		[productCategory]
	);

	const expandSizeValid =
		!selectedFeatures.includes("expand") ||
		(Number(featureOptions.expandWidth) > 0 &&
			Number(featureOptions.expandHeight) > 0);
	const applyDisabled =
		!imageFile || loading || selectedFeatures.length === 0 || !expandSizeValid;
	const generateDisabled = !genPrompt.trim() || loading;
	const hasGeneratedResults =
		mode === "generate" && generatedUrls.length > 0 && generatedUrls.some(Boolean);
	const hasProcessedResults = processedUrls.length > 0 && processedUrls.some(Boolean);

	return {
		mode,
		imageFile,
		previewUrl,
		selectedFeatures,
		featureOptions,
		genPrompt,
		genSeed,
		catalogPromptFromProduct,
		generatedUrls,
		useExportAndResolution,
		exportFormat,
		exportQuality,
		globalOutputSize,
		maxWidth,
		maxHeight,
		referenceBox,
		loading,
		error,
		processedUrls,
		isDragOver,
		expandAiBackgroundConflictNote,
		fileInputRef,
		features,
		matchedCategory,
		categoryPrompts,
		expandSizeValid,
		applyDisabled,
		generateDisabled,
		hasGeneratedResults,
		hasProcessedResults,
		setMode,
		setGenPrompt,
		setGenSeed,
		setUseExportAndResolution,
		setExportFormat,
		setExportQuality,
		setGlobalOutputSize,
		setMaxWidth,
		setMaxHeight,
		setReferenceBox,
		setIsDragOver,
		handleFile,
		onFileChange,
		onDrop,
		clearImage,
		getOptionsForFeature,
		toggleFeature,
		handleApply,
		handleApplyGenerate,
		handleDownload,
		setOption,
		switchToEnhance,
		switchToGenerate,
		resetGenerated,
		resetProcessed,
	};
}
